import { useEffect, useLayoutEffect, useState, useRef } from "react"
import { isEqual, omit } from "lodash"
import { makeFindAndGetBaseMetadata } from "helpers/base"
import { ERROR, OK } from "helpers/data-statuses"
import type { PreferencesValidationOptions } from "hooks/use-preferences/types"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useActiveInstruments } from "resources/instruments"
import { useDashboardId } from "../../context/dashboard-id"
import { coreSlice } from "../../store/core-slice"
import { useGetUrlInstrumentParams, type UrlInstrument } from "../use-get-instrument-url-params"
import { resolveUrlInstrumentOverride } from "./resolve-url-instrument-override"
import { validateDashboardPreferences } from "./validate-dashboard-preferences"

function getUrlInstrumentKey(urlInstrument: UrlInstrument | undefined) {
  const { urlInstrumentType, urlInstrumentId } = urlInstrument ?? {}
  if (!urlInstrumentType || !urlInstrumentId) {
    return null
  }
  return `${urlInstrumentType}:${urlInstrumentId}`
}

export function useDashboardPreferencesValidation(modelId: string | null) {
  const dashboardId = useDashboardId()
  const dispatch = useAppDispatch()
  const [activeInstruments, activeInstrumentsStatus] = useActiveInstruments(modelId, true)
  const [preferenceValidationOptions, setPreferenceValidationOptions] = useState<PreferencesValidationOptions | null>(null)
  const { instrument: urlInstrument, clearInstrumentFromUrl } = useGetUrlInstrumentParams()
  const currentInstrument = useAppSelector((state) => state.dashboard.core[dashboardId]?.instrument)

  const urlInstrumentKey = getUrlInstrumentKey(urlInstrument)
  const lastValidatedResultRef = useRef<unknown | null>(null)
  const dispatchedUrlInstrumentKeyRef = useRef<string | null>(null)
  const validatedUrlInstrumentKeyRef = useRef<string | null>(null)
  const hasCompletedInitialValidationRef = useRef(false)

  useLayoutEffect(() => {
    if (urlInstrument?.urlInstrumentType && urlInstrument?.urlInstrumentId) {
      clearInstrumentFromUrl()
    }
  }, [urlInstrument, clearInstrumentFromUrl])

  useEffect(() => {
    if (!hasCompletedInitialValidationRef.current || !urlInstrumentKey) {
      return
    }
    if (urlInstrumentKey === dispatchedUrlInstrumentKeyRef.current) {
      return
    }
    if (!modelId || activeInstrumentsStatus !== OK || !activeInstruments) {
      return
    }

    const instrumentValidator = makeFindAndGetBaseMetadata(null, activeInstruments)
    const overrideMainInstrument = resolveUrlInstrumentOverride(
      urlInstrument,
      activeInstruments,
      instrumentValidator,
    )
    if (!overrideMainInstrument) {
      return
    }

    dispatchedUrlInstrumentKeyRef.current = urlInstrumentKey
    dispatch(coreSlice.actions.setInstrument({ dashboardId, instrument: overrideMainInstrument }))
  }, [
    urlInstrumentKey,
    urlInstrument,
    modelId,
    activeInstrumentsStatus,
    activeInstruments,
    dashboardId,
    dispatch,
  ])

  useEffect(() => {
    // Without a model, instrument validation is impossible. Fire the callback
    // immediately so the dashboard preferences loading gate is not blocked while
    // model preferences are still in-flight. Validation will re-run once modelId
    // arrives and instruments finish loading.
    const isReady = !modelId || activeInstrumentsStatus === OK || activeInstrumentsStatus === ERROR
    if (!preferenceValidationOptions || !isReady) {
      return
    }

    const { preferences, callback } = preferenceValidationOptions

    const preferencesForDashboard = typeof preferences.modelId === "string"
      ? omit(preferences, "modelId")
      : preferences

    let validationResult = {
      isCorrected: false,
      preferences: preferencesForDashboard,
    }

    if (modelId && activeInstrumentsStatus === OK && activeInstruments) {
      const shouldApplyUrlOverride = Boolean(
        urlInstrumentKey && urlInstrumentKey !== validatedUrlInstrumentKeyRef.current,
      )
      let preferencesInput = preferencesForDashboard

      if (
        !shouldApplyUrlOverride
        && urlInstrumentKey
        && urlInstrumentKey === validatedUrlInstrumentKeyRef.current
        && currentInstrument
      ) {
        preferencesInput = { ...preferencesForDashboard, instrument: currentInstrument }
      }

      validationResult = validateDashboardPreferences(
        preferencesInput,
        activeInstruments,
        shouldApplyUrlOverride ? { urlInstrument } : {},
      )

      if (shouldApplyUrlOverride) {
        const overrideMainInstrument = resolveUrlInstrumentOverride(
          urlInstrument,
          activeInstruments,
          makeFindAndGetBaseMetadata(null, activeInstruments),
        )
        if (overrideMainInstrument) {
          lastValidatedResultRef.current = null
          if (isEqual(validationResult.preferences.instrument, overrideMainInstrument)) {
            validatedUrlInstrumentKeyRef.current = urlInstrumentKey
          }
        } else {
          validatedUrlInstrumentKeyRef.current = urlInstrumentKey
        }
      }
    }

    if (!isEqual(validationResult, lastValidatedResultRef.current)) {
      callback(validationResult)
    }
    lastValidatedResultRef.current = validationResult
    hasCompletedInitialValidationRef.current = true
  }, [
    activeInstrumentsStatus,
    preferenceValidationOptions,
    activeInstruments,
    modelId,
    urlInstrument,
    urlInstrumentKey,
    currentInstrument,
  ])

  return setPreferenceValidationOptions
}
